/**
 * Mock route handlers.
 * Each handler receives (method, url, data) and returns a mock response body.
 * Return `null` to fall through to the next handler (→ 404).
 */

import {
  MOCK_ITEMS,
  MOCK_AUTH_USER,
  MOCK_TOKEN,
  MOCK_USERS,
  MOCK_HISTORY,
} from "./data";

// Local in-memory store so Create / Update / Delete work during a session
let items = [...MOCK_ITEMS];

// Per-user reminder settings store (mock — resets on page refresh)
let mockReminderSettings = {
  reminder_days: [30, 7, 1] as number[],
  email_enabled: true,
  send_hour: 9,
};

// UUID pattern for route matching (standard UUID format)
const UUID_RE = "[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}";

// Helper to extract a UUID from the URL at a given position (e.g. /expiry/{uuid})
const extractId = (url: string, prefix: string): string | null => {
  const re = new RegExp(`${prefix}/(${UUID_RE})`, "i");
  const m = url.match(re);
  return m ? m[1] : null;
};

// Archived items store (separate from active items)
let archivedItems: any[] = [];

// ─── History store ────────────────────────────────────────────────────────────
// Deep-copy so mutations during the session don't alter the imported constant
const history: Record<string, any[]> = Object.fromEntries(
  Object.entries(MOCK_HISTORY).map(([k, v]) => [k, [...v]])
);

const getHistory = (id: string) => history[id] ?? [];

const addHistoryEvent = (itemId: string, event: Omit<any, "id">) => {
  if (!history[itemId]) history[itemId] = [];
  // Generate a random UUID-like ID for history events
  const hid =
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `hist-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  history[itemId] = [{ ...event, id: hid }, ...history[itemId]];
};

// Attach history to an item object before returning it
const withHistory = (item: any) => ({ ...item, history: getHistory(item.id) });

// Generate a new UUID for newly created mock items
const newUUID = (): string =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `mock-${Date.now()}-${Math.random().toString(36).slice(2)}`;

// ─── Types ────────────────────────────────────────────────────────────────────
export interface MockResponse {
  status: number;
  data: unknown;
}

type HandlerFn = (
  method: string,
  url: string,
  requestData: unknown
) => MockResponse | null;

// ─── Helpers ──────────────────────────────────────────────────────────────────
const ok = (data: unknown, status = 200): MockResponse => ({ status, data });
const err = (msg: string, status = 400): MockResponse => ({
  status,
  data: { error: msg },
});

// ─── Owner resolver ───────────────────────────────────────────────────────────
// Accepts:
//   owner_emails: string[]  — from ItemModal chip input (and CSV import fallback)
//   owner_email:  string    — from CSV import (single email)
//   owner_id:     string    — legacy fallback (UUID)
const resolveOwners = (data: any): typeof MOCK_USERS => {
  // Array of emails (chip input)
  if (Array.isArray(data.owner_emails) && data.owner_emails.length > 0) {
    return data.owner_emails
      .map((email: string) => MOCK_USERS.find((u) => u.email === email))
      .filter(Boolean) as typeof MOCK_USERS;
  }
  // Single email string (CSV import)
  if (data.owner_email) {
    const u = MOCK_USERS.find((u) => u.email === data.owner_email);
    return u ? [u] : [];
  }
  // Legacy id fallback (UUID string)
  if (data.owner_id) {
    const u = MOCK_USERS.find((u) => u.id === data.owner_id);
    return u ? [u] : [];
  }
  return [];
};

// ─── Route table ─────────────────────────────────────────────────────────────
const handlers: HandlerFn[] = [
  // ── Auth ──────────────────────────────────────────────────────────────────

  (method, url) => {
    if (method === "post" && url.endsWith("/auth/check-email")) {
      // Any email returns "exists: true" in mock mode so login works
      return ok({ exists: true, user: MOCK_AUTH_USER });
    }
    return null;
  },

  (method, url, data: any) => {
    if (method === "post" && url.endsWith("/auth/login")) {
      return ok({ user: MOCK_AUTH_USER, token: MOCK_TOKEN });
    }
    return null;
  },

  (method, url, data: any) => {
    if (method === "post" && url.endsWith("/auth/register")) {
      const user = {
        ...MOCK_AUTH_USER,
        email: (data as any)?.email || MOCK_AUTH_USER.email,
        name: (data as any)?.name || MOCK_AUTH_USER.name,
      };
      return ok({ user, token: MOCK_TOKEN }, 201);
    }
    return null;
  },

  (method, url) => {
    if (method === "get" && url.endsWith("/auth/me")) {
      return ok(MOCK_AUTH_USER);
    }
    return null;
  },

  // ── Expiry Items ──────────────────────────────────────────────────────────

  (method, url) => {
    // GET /expiry/expiring-soon?days=N
    if (method === "get" && url.includes("/expiry/expiring-soon")) {
      const match = url.match(/days=(\d+)/);
      const days = match ? parseInt(match[1]) : 7;
      const now = new Date();
      const result = items.filter((item) => {
        if (!item.expiry_date) return false;
        const d = new Date(item.expiry_date);
        const diff = Math.ceil((d.getTime() - now.getTime()) / 86400000);
        return diff >= 0 && diff <= days;
      });
      return ok(result);
    }
    return null;
  },

  (method, url) => {
    // GET /expiry/archived
    if (method === "get" && /\/expiry\/archived\/?$/.test(url)) {
      return ok(archivedItems.map(withHistory));
    }
    return null;
  },

  (method, url) => {
    // POST /expiry/:uuid/archive  — move active → archived
    const id = extractId(url, "/expiry");
    if (method === "post" && id && url.endsWith("/archive")) {
      const item = items.find((i) => i.id === id);
      if (!item) return err("Not found", 404);
      items = items.filter((i) => i.id !== id);
      archivedItems = [
        { ...item, archived_at: new Date().toISOString() },
        ...archivedItems,
      ];
      addHistoryEvent(id, {
        type: "edited",
        description: "📦 Archived — moved to archive",
        date: new Date().toISOString(),
        user: MOCK_AUTH_USER.name,
      });
      return ok({ message: "Archived successfully" });
    }
    return null;
  },

  (method, url) => {
    // POST /expiry/:uuid/restore  — move archived → active
    const id = extractId(url, "/expiry");
    if (method === "post" && id && url.endsWith("/restore")) {
      const item = archivedItems.find((i) => i.id === id);
      if (!item) return err("Not found", 404);
      const { archived_at, ...restored } = item;
      archivedItems = archivedItems.filter((i) => i.id !== id);
      items = [{ ...restored, updated_at: new Date().toISOString() }, ...items];
      addHistoryEvent(id, {
        type: "edited",
        description: "↩ Restored from archive",
        date: new Date().toISOString(),
        user: MOCK_AUTH_USER.name,
      });
      return ok({ message: "Restored successfully" });
    }
    return null;
  },

  (method, url) => {
    // POST /expiry/:uuid/send-reminder
    const id = extractId(url, "/expiry");
    if (method === "post" && id && url.endsWith("/send-reminder")) {
      const item = items.find((i) => i.id === id);
      if (!item) return err("Not found", 404);

      const owners: typeof MOCK_USERS = (item as any).owners ?? [];
      if (owners.length === 0) {
        return err(
          "No owners assigned to this document — open the document to add one",
          400
        );
      }

      const recipients = owners
        .map((o: any) => o.email)
        .filter(Boolean) as string[];

      addHistoryEvent(id, {
        type: "edited",
        description: `📧 Manual reminder sent to: ${recipients.join(", ")}`,
        date: new Date().toISOString(),
        user: MOCK_AUTH_USER.name,
      });

      return ok({ sent: recipients.length, recipients });
    }
    return null;
  },

  (method, url) => {
    // GET /expiry/history  — all history for audit trail (must be before /:id)
    if (method === "get" && /\/expiry\/history\/?(\?.*)?$/.test(url)) {
      // Flatten all history entries, attach item_name & item_category from MOCK_ITEMS
      const allEvents: any[] = [];
      for (const item of MOCK_ITEMS) {
        const itemHistory = history[item.id] ?? [];
        for (const ev of itemHistory) {
          allEvents.push({
            ...ev,
            item_id: item.id,
            item_name: item.name,
            item_category: item.category,
          });
        }
      }
      // Sort newest first
      allEvents.sort(
        (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
      );
      return ok(allEvents.slice(0, 200));
    }
    return null;
  },

  (method, url) => {
    // GET /expiry/:uuid/history  — must be checked BEFORE GET /expiry/:uuid
    const histMatch = url.match(/\/expiry\/([0-9a-fA-F-]+)\/history$/);
    if (method === "get" && histMatch) {
      return ok(getHistory(histMatch[1]));
    }
    return null;
  },

  (method, url) => {
    // GET /expiry/:uuid
    const id = extractId(url, "/expiry");
    if (method === "get" && id) {
      const item = items.find((i) => i.id === id);
      return item ? ok(withHistory(item)) : err("Not found", 404);
    }
    return null;
  },

  (method, url) => {
    // GET /expiry
    if (method === "get" && /\/expiry\/?$/.test(url)) {
      return ok(items.map(withHistory));
    }
    return null;
  },

  (method, url, data: any) => {
    // POST /expiry/import-csv — bulk import from raw CSV text
    if (method === "post" && url.endsWith("/expiry/import-csv")) {
      const csvText: string = data?.csv ?? "";
      if (!csvText.trim()) return err("csv field is required", 400);

      // Minimal client-side parse for mock: split lines, skip header
      const lines = csvText.split(/\r?\n/).filter((l: string) => l.trim());
      if (lines.length < 2) return err("No data rows found", 422);

      const inserted: any[] = [];
      const draft: any[] = [];

      lines.slice(1).forEach((line: string, i: number) => {
        // Simple CSV split (handles basic quoted fields)
        const cols: string[] = [];
        let cur = "";
        let inQ = false;
        for (const ch of line) {
          if (ch === '"') {
            inQ = !inQ;
          } else if (ch === "," && !inQ) {
            cols.push(cur.trim());
            cur = "";
          } else cur += ch;
        }
        cols.push(cur.trim());

        const [
          name = "",
          category = "",
          owner_email = "",
          expiry_date = "",
          notes = "",
        ] = cols;
        if (!name) return; // skip empty rows

        const missingFields = [];
        if (!expiry_date) missingFields.push("expiry_date");
        if (!category) missingFields.push("category");

        const newId = newUUID();
        const resolvedOwners = owner_email
          ? MOCK_USERS.filter((u) => u.email === owner_email)
          : [];

        const newItem = {
          id: newId,
          name,
          category: category?.trim() || "Others",
          expiry_date: expiry_date || null,
          quantity: null,
          unit: null,
          notes: notes || null,
          owners: resolvedOwners,
          is_draft: missingFields.length > 0,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (items as any[]).unshift(newItem);
        addHistoryEvent(newId, {
          type: "created",
          description:
            missingFields.length > 0
              ? `Imported as draft (missing: ${missingFields.join(", ")})`
              : "Imported from CSV",
          date: newItem.created_at,
          user: MOCK_AUTH_USER.name,
        });

        if (missingFields.length > 0) draft.push(newItem);
        else inserted.push(newItem);
      });

      const total = inserted.length + draft.length;
      return ok(
        {
          imported: total,
          ready: inserted.length,
          draft: draft.length,
          errors: [],
          items: [...inserted, ...draft],
        },
        201
      );
    }
    return null;
  },

  (method, url, data: any) => {
    // POST /expiry
    if (method === "post" && /\/expiry\/?$/.test(url)) {
      const newId = newUUID();
      const resolvedOwners = resolveOwners(data);
      const newItem = {
        id: newId,
        name: data.name,
        category: data.category,
        expiry_date: data.expiry_date || null,
        quantity: null,
        unit: null,
        notes: data.notes || null,
        owners: resolvedOwners,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      items = [newItem, ...items];
      addHistoryEvent(newId, {
        type: "created",
        description: "Document created",
        date: new Date().toISOString(),
        user: MOCK_AUTH_USER.name,
      });
      return ok(withHistory(newItem), 201);
    }
    return null;
  },

  (method, url, data: any) => {
    // PUT /expiry/:uuid
    const id = extractId(url, "/expiry");
    if (method === "put" && id) {
      const oldItem = items.find((i) => i.id === id);
      const { isRenewal, isExpiryEdit, ...cleanData } = data; // strip flags before saving
      const resolvedOwners = resolveOwners(cleanData);

      items = items.map((item) =>
        item.id === id
          ? {
              ...item,
              ...cleanData,
              owners: resolvedOwners,
              id,
              updated_at: new Date().toISOString(),
            }
          : item
      );

      if (isRenewal && oldItem) {
        addHistoryEvent(id, {
          type: "renewed",
          description: "Document renewed — reminder cycle reset",
          date: new Date().toISOString(),
          user: MOCK_AUTH_USER.name,
          meta: {
            previousDate: oldItem.expiry_date || "—",
            newDate: cleanData.expiry_date || "—",
          },
        });
      } else if (cleanData.snoozed_until !== undefined && oldItem) {
        // Snooze action
        const snoozeDesc = cleanData.snoozed_until
          ? `Reminders snoozed until ${new Date(cleanData.snoozed_until + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`
          : "Reminder snooze cleared";
        addHistoryEvent(id, {
          type: "snoozed",
          description: snoozeDesc,
          date: new Date().toISOString(),
          user: MOCK_AUTH_USER.name,
          meta: { snoozed_until: cleanData.snoozed_until },
        });
      } else if (oldItem) {
        // --- Expiry date directly edited (pencil icon, not a renewal) ---
        if (isExpiryEdit && cleanData.expiry_date !== oldItem.expiry_date) {
          const fmt = (d: string | null | undefined) =>
            d
              ? new Date(d).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })
              : "—";
          addHistoryEvent(id, {
            type: "edited",
            description: `Expiry date updated: ${fmt(oldItem.expiry_date)} → ${fmt(cleanData.expiry_date)}`,
            date: new Date().toISOString(),
            user: MOCK_AUTH_USER.name,
            meta: {
              field: "expiry_date",
              previousDate: oldItem.expiry_date || "—",
              newDate: cleanData.expiry_date || "—",
            },
          });
        }

        // --- Other field changes (name, category, notes, expiry in non-edit mode) ---
        const changedFields: string[] = [];
        if (cleanData.name && cleanData.name !== oldItem.name)
          changedFields.push("name");
        if (cleanData.category && cleanData.category !== oldItem.category)
          changedFields.push("category");
        if (cleanData.notes !== undefined && cleanData.notes !== oldItem.notes)
          changedFields.push("notes");
        // Expiry date change without either flag (e.g. bulk renew / add-fields)
        if (
          !isExpiryEdit &&
          cleanData.expiry_date !== undefined &&
          cleanData.expiry_date !== oldItem.expiry_date
        ) {
          changedFields.push("expiry date");
        }
        if (changedFields.length > 0) {
          addHistoryEvent(id, {
            type: "edited",
            description: `Updated: ${changedFields.join(", ")}`,
            date: new Date().toISOString(),
            user: MOCK_AUTH_USER.name,
          });
        }
      }

      return ok(withHistory(items.find((i) => i.id === id)));
    }
    return null;
  },

  (method, url) => {
    // DELETE /expiry/:uuid
    const id = extractId(url, "/expiry");
    if (method === "delete" && id) {
      items = items.filter((i) => i.id !== id);
      return ok({ message: "Deleted successfully" });
    }
    return null;
  },

  // ── Users ─────────────────────────────────────────────────────────────────

  (method, url) => {
    if (method === "get" && url.endsWith("/users")) {
      return ok(MOCK_USERS);
    }
    return null;
  },

  // ── Notifications (reminder_sent events, most-recent first) ───────────────

  (method, url) => {
    if (method === "get" && url.endsWith("/notifications")) {
      // Build a flat list of all reminder_sent events across all items
      const notifItems = MOCK_ITEMS.slice(0, 6); // first 6 items with history
      const events: any[] = [];

      for (const item of notifItems) {
        const itemHistory: any[] = history[item.id] ?? [];
        for (const ev of itemHistory) {
          if (ev.type === "reminder_sent") {
            events.push({
              id: ev.id,
              type: ev.type,
              description: ev.description,
              date: ev.date,
              meta: ev.meta ?? {},
              item_id: item.id,
              item_name: item.name,
            });
          }
        }
      }

      // Sort newest first
      events.sort(
        (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
      );
      // Limit to 50
      return ok(events.slice(0, 50));
    }
    return null;
  },

  // ── Settings / Reminder Schedule ──────────────────────────────────────────

  (method, url, data: any) => {
    if (url.endsWith("/settings/reminders")) {
      if (method === "get") {
        return ok({
          user_id: MOCK_AUTH_USER.id,
          reminder_days: mockReminderSettings.reminder_days,
          email_enabled: mockReminderSettings.email_enabled,
          send_hour: mockReminderSettings.send_hour,
          timezone: "UTC",
          from_name: null,
          reply_to: null,
          custom_subject: null,
          custom_body_html: null,
        });
      }
      if (method === "put") {
        if (data?.reminder_days !== undefined)
          mockReminderSettings.reminder_days = data.reminder_days;
        if (data?.email_enabled !== undefined)
          mockReminderSettings.email_enabled = data.email_enabled;
        if (data?.send_hour !== undefined)
          mockReminderSettings.send_hour = data.send_hour;
        return ok({
          ...mockReminderSettings,
          user_id: MOCK_AUTH_USER.id,
          timezone: "UTC",
        });
      }
    }
    return null;
  },

  // ── Billing (Stripe) — disabled in mock mode ────────────────────────────────
  (method, url) => {
    if (url.includes("/billing/config") && method === "get") {
      return ok({
        enabled: false,
        intervals: { month: true, year: false },
        plans: [
          { key: "pro", label: "Pro" },
          { key: "business", label: "Business" },
        ],
      });
    }
    if (url.includes("/billing/checkout-session") && method === "post") {
      return err("Stripe checkout not available in mock mode", 503);
    }
    if (url.includes("/billing/portal-session") && method === "post") {
      return err("Billing portal not available in mock mode", 503);
    }
    return null;
  },
];

// ─── Dispatcher ───────────────────────────────────────────────────────────────
export function dispatch(
  method: string,
  url: string,
  data: unknown
): MockResponse {
  const m = method.toLowerCase();
  // Strip query-string for matching (keep for expiring-soon)
  const cleanUrl = url.split("?")[0];
  const fullUrl = url; // pass full for query-string handlers

  for (const handler of handlers) {
    const result = handler(m, fullUrl, data) ?? handler(m, cleanUrl, data);
    if (result) return result;
  }

  console.warn(`[mock] Unhandled request: ${method.toUpperCase()} ${url}`);
  return err(`No mock handler for ${method.toUpperCase()} ${url}`, 404);
}
